import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import PageTabs from 'components/PageTabs';
import { FixedCenterLoader } from 'components/Loaders';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import DownloadSimpleTable from 'components/DownloadSimpleTable';
import ReactDatetime from 'react-datetime';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { listProducts, getDiscovery, toolboxCorrelation, getQuickViewData } from 'services/analysis';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { confidenceLevelToInterval } from 'helpers/formatter';
import { toDeci, toPerc, toPercTable, toDeciTable, tablePresets, getDataType, formatCurrency } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import TightTable from 'components/TightTable';
import { Descriptions, Notes } from '../others';
import SimpleTable from 'components/SimpleTable';
import { toAggregatedDataFrame, sortDataFrameByIndex, sortDataFrameByColumns } from 'helpers/data-frame';

function DatabaseDetails({ database, product, data }) {
  const tableKeys = Object.keys(data.Summary);

  return (
    <>
      <AutoColumns>
        {tableKeys.map((key, ind) => {
          const tableData = data.Summary[key];
          return (
            <Card>
              <CardHeader tag="h4" className="mt-0 text-center">
                {key}
              </CardHeader>
              <CardBody>
                <DownloadSimpleTable data={tableData} suffix={`quick-view-details-${key}`}></DownloadSimpleTable>
                <Table bordered striped>
                  <tbody>
                    {tableData.index.map((row, i) => {
                      const value = tableData.data[i][0];
                      if (!value) return null;

                      return (
                        <tr>
                          <td>{row}</td>
                          <td>{value}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          );
        })}
      </AutoColumns>
    </>
  );
}

function UserSavedDetails({ database, product, data }) {
  const [typeOptions, setTypeOptions, selectedTypeOption, setSelectedTypeOption] = useArrayState({
    initialItems: optionizeAll(['Holdings', 'Category', 'Sub-Category', 'Type', 'Jurisdiction', 'Currency']),
  });

  const { chartData, tableData } = useMemo(() => {
    if (!data.portfolio_comp || !selectedTypeOption) return {};

    const keyIndex = data.portfolio_comp.columns.findIndex((col) => col === selectedTypeOption.value);
    const weightIndex = data.portfolio_comp.columns.findIndex((col) => col === 'Weight');
    const duplicatedKeys = [];
    const weights = [];

    data.portfolio_comp.data.forEach((row) => {
      duplicatedKeys.push(row[keyIndex]);
      weights.push(row[weightIndex]);
    });

    const isHoldings = selectedTypeOption.value === 'Holdings';

    const chartData = sortDataFrameByIndex(
      toAggregatedDataFrame(selectedTypeOption.value, isHoldings ? data.portfolio_comp.index : duplicatedKeys, weights),
    );

    let tableData = null;
    if (isHoldings) {
      tableData = sortDataFrameByColumns(data.portfolio_comp, ['Category', 'Weight'], ['asc', 'desc']);
    } else {
      tableData = sortDataFrameByIndex(chartData);
    }

    return { chartData, tableData };
  }, [data.portfolio_comp, selectedTypeOption]);

  if (!chartData || !tableData) {
    return null;
  }

  return (
    <>
      <div className="tw-max-w-xl tw-mx-auto">
        <div className="tw-text-left tw-font-bold">SELECT</div>
        <Select
          className="react-select tw-max-w-xs"
          name="selectedTypeOption"
          value={selectedTypeOption}
          onChange={setSelectedTypeOption}
          options={typeOptions}
        />

        <Card>
          <CardHeader tag="h4" className="tw-mt-0 tw-text-center tw-text-lg">
            <div>{product}</div>
          </CardHeader>
          <CardBody>
            <PieChart
              title={product}
              data={chartData}
              field="Weight"
              dataType="percentage"
              containerStyle={{ maxWidth: '400px', maxHeight: '400px' }}
              options={{
                plugins: {
                  legend: {
                    display: chartData.index.length <= 6,
                    position: 'bottom',
                  },
                },
                layout: {
                  padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                  },
                },
              }}
              // containerStyle={{ maxWidth: '1000px', maxHeight: '500px' }}
            />
          </CardBody>
        </Card>
      </div>

      <div className={classnames('tw-mx-auto', selectedTypeOption.value === 'Holdings' ? '' : 'tw-max-w-xl')}>
        <Card>
          <CardHeader tag="h4" className="mt-0 text-center">
            <div>{product}</div>
          </CardHeader>
          <CardBody>
            <TightTable
              suffix="quickview-details"
              data={tableData}
              indexName={selectedTypeOption.value}
              cellFormatter={(value, row, col) => {
                if (col === 'Weight') return toPercTable(value);
                return value;
              }}
              indexWidth="300px"
              compact
              useSort
              initialSortBy={tableData.columns.findIndex((col) => col === 'Weight') + 1}
              initialSortOrder="desc"
            />
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Details({ database, product, converter, setName }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    const [data, err] = await getQuickViewData({
      database,
      product,
      converter,
      page_choice: 'details',
    });

    if (data) {
      setResult(data);
      setName(data.formal_name);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!database || !product) return;

    fetchData();
  }, [database, product, converter]);

  if (loading) {
    return <FixedCenterLoader />;
  }

  if (!result) {
    return null;
  }

  let main = null;

  if (database === 'Saved Portfolios' || database === 'Model Portfolios') {
    main = <UserSavedDetails database={database} product={product} data={result} />;
  } else if (database === 'Uploaded Assets') {
    main = (
      <Card>
        <CardHeader tag="h4" className="mt-0 text-center">
          Detail
        </CardHeader>
        <CardBody>
          <SimpleTable data={result} showHeaders={false} suffix="uploaded-assets" />;
        </CardBody>
      </Card>
    );
  } else {
    main = <DatabaseDetails database={database} product={product} data={result} />;
  }

  return (
    <>
      <Descriptions content={result.descriptions} />
      {main}
      <Notes content={result.notes} />
    </>
  );
}

export default Details;
